"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChainableIterator = exports.EmptyIteratorError = void 0;
class EmptyIteratorError extends Error {
    constructor(message) {
        super(message ?? 'Expected iterator to yield at least one element');
        this.name = 'EmptyIteratorError';
    }
}
exports.EmptyIteratorError = EmptyIteratorError;
class ChainableIterator {
    constructor(source) {
        this.source = source;
    }
    static from(iterable) {
        return new ChainableIterator(iterable);
    }
    [Symbol.iterator]() {
        return this.source[Symbol.iterator]();
    }
    pipeMap(fn) {
        const source = this.source;
        return new ChainableIterator((function* () {
            let i = 0;
            for (const item of source) {
                yield fn(item, i++);
            }
        })());
    }
    pipeFilter(fn) {
        const source = this.source;
        return new ChainableIterator((function* () {
            let i = 0;
            for (const item of source) {
                if (fn(item, i++)) {
                    yield item;
                }
            }
        })());
    }
    pipeFlatMap(fn) {
        const source = this.source;
        return new ChainableIterator((function* () {
            let i = 0;
            for (const item of source) {
                yield* fn(item, i++);
            }
        })());
    }
    pipeTake(n) {
        const source = this.source;
        return new ChainableIterator((function* () {
            if (n <= 0) {
                return;
            }
            let count = 0;
            const iter = source[Symbol.iterator]();
            let result = iter.next();
            while (!result.done) {
                yield result.value;
                count++;
                if (count >= n) {
                    return;
                }
                result = iter.next();
            }
        })());
    }
    pipeSkip(n) {
        const source = this.source;
        return new ChainableIterator((function* () {
            let skipped = 0;
            for (const item of source) {
                if (skipped < n) {
                    skipped++;
                    continue;
                }
                yield item;
            }
        })());
    }
    pipeEnumerate() {
        const source = this.source;
        return new ChainableIterator((function* () {
            let i = 0;
            for (const item of source) {
                yield [i++, item];
            }
        })());
    }
    pipeUnique(keyFn) {
        const source = this.source;
        return new ChainableIterator((function* () {
            const seen = new Set();
            for (const item of source) {
                const key = keyFn ? keyFn(item) : item;
                if (!seen.has(key)) {
                    seen.add(key);
                    yield item;
                }
            }
        })());
    }
    pipeConcat(...others) {
        const source = this.source;
        return new ChainableIterator((function* () {
            yield* source;
            for (const other of others) {
                yield* other;
            }
        })());
    }
    pipeAssertAtLeastOne(message) {
        const source = this.source;
        return new ChainableIterator((function* () {
            let yielded = false;
            for (const item of source) {
                yielded = true;
                yield item;
            }
            if (!yielded) {
                throw new EmptyIteratorError(message);
            }
        })());
    }
    pipeZip(other) {
        const source = this.source;
        return new ChainableIterator((function* () {
            const iterA = source[Symbol.iterator]();
            const iterB = other[Symbol.iterator]();
            while (true) {
                const a = iterA.next();
                const b = iterB.next();
                if (a.done || b.done) {
                    break;
                }
                yield [a.value, b.value];
            }
        })());
    }
    reduce(fn, initial) {
        let acc = initial;
        let i = 0;
        for (const item of this.source) {
            acc = fn(acc, item, i++);
        }
        return acc;
    }
    forEach(fn) {
        let i = 0;
        for (const item of this.source) {
            fn(item, i++);
        }
    }
    find(fn) {
        let i = 0;
        for (const item of this.source) {
            if (fn(item, i++)) {
                return item;
            }
        }
        return undefined;
    }
    some(fn) {
        let i = 0;
        for (const item of this.source) {
            if (fn(item, i++)) {
                return true;
            }
        }
        return false;
    }
    every(fn) {
        let i = 0;
        for (const item of this.source) {
            if (!fn(item, i++)) {
                return false;
            }
        }
        return true;
    }
    includes(value) {
        for (const item of this.source) {
            if (item === value) {
                return true;
            }
        }
        return false;
    }
    atLeastOne() {
        for (const _ of this.source) {
            return true;
        }
        return false;
    }
    count() {
        let n = 0;
        for (const _ of this.source) {
            n++;
        }
        return n;
    }
    first() {
        for (const item of this.source) {
            return item;
        }
        return undefined;
    }
    toArray() {
        return Array.from(this.source);
    }
    toSet() {
        return new Set(this.source);
    }
    toMap() {
        return new Map(this.source);
    }
    join(separator = ',') {
        let result = '';
        let first = true;
        for (const item of this.source) {
            if (!first) {
                result += separator;
            }
            result += String(item);
            first = false;
        }
        return result;
    }
    groupBy(keyFn) {
        const groups = new Map();
        for (const item of this.source) {
            const key = keyFn(item);
            const group = groups.get(key);
            if (group) {
                group.push(item);
            }
            else {
                groups.set(key, [item]);
            }
        }
        return groups;
    }
    partition(fn) {
        const pass = [];
        const fail = [];
        for (const item of this.source) {
            if (fn(item)) {
                pass.push(item);
            }
            else {
                fail.push(item);
            }
        }
        return [pass, fail];
    }
}
exports.ChainableIterator = ChainableIterator;
//# sourceMappingURL=ChainableIterator.js.map